import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, CalendarDays, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero, EmptyState, LazyImg, ShareButtons, HtmlBlock } from "../../components/Bits";

export function Projects() {
  const { t, L, lang } = useApp();
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/public/projects?page=${page}&limit=9`).then((r) => setData(r.data)).catch(() => {});
  }, [page]);

  return (
    <div data-testid="projects-page">
      <SEO title={t("projects")} />
      <PageHero title={t("projects")} sub={lang === "hi" ? "फाउंडेशन की चल रही और पूर्ण परियोजनाएँ।" : "Ongoing and completed programmes of the Foundation."} />
      <section className="py-16">
        <div className="container-ssf">
          {data.items.length === 0 && <EmptyState text="Projects will appear here once published." />}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.items.map((p) => (
              <Link key={p.id} to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`}
                className="card-ssf group flex flex-col overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-xl">
                {p.image && <LazyImg src={p.image} alt={p.title_en} className="aspect-[16/9] w-full object-cover" />}
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-saffron-600">{p.category}</span>
                  <h2 className="mt-2 font-display text-xl font-bold text-navy group-hover:text-saffron-600">{L(p, "title")}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{L(p, "short_desc") || (p.description_en || "").replace(/<[^>]+>/g, "").slice(0, 140)}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                    {p.location && <span className="inline-flex items-center gap-1"><MapPin size={13} />{p.location}</span>}
                    {p.start_date && <span className="inline-flex items-center gap-1"><CalendarDays size={13} />{p.start_date}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {data.total > 9 && (
            <div className="mt-10 flex justify-center gap-3">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="btn-ghost !px-5 !py-2 text-sm disabled:opacity-40" data-testid="projects-prev">←</button>
              <span className="py-2 text-sm font-semibold text-navy">{page}</span>
              <button disabled={page * 9 >= data.total} onClick={() => setPage(page + 1)} className="btn-ghost !px-5 !py-2 text-sm disabled:opacity-40" data-testid="projects-next">→</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function ProjectDetail() {
  const { slug } = useParams();
  const { L, t } = useApp();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public/projects/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <PageHero title={t("not_found")} />;
  if (!p) return null;

  return (
    <div data-testid="project-detail">
      <SEO title={p.seo_title || p.title_en} description={p.seo_desc || p.short_desc_en} image={p.image} keywords={p.seo_keywords} />
      <PageHero title={L(p, "title")} sub={L(p, "short_desc")} />
      <section className="py-14">
        <div className="container-ssf max-w-4xl">
          {p.image && <LazyImg src={p.image} alt={p.alt_text || p.title_en} className="w-full rounded-2xl object-cover" />}
          <HtmlBlock html={L(p, "description") || p.description_en || ""} className="mt-8 max-w-none text-slate-700" testid="project-description" />
          {(L(p, "objectives") || L(p, "activities")) && (
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {L(p, "objectives") && <div className="card-ssf p-6"><p className="label-eyebrow">Objectives</p><HtmlBlock html={L(p, "objectives")} className="mt-2 text-sm text-slate-600" testid="project-objectives" /></div>}
              {L(p, "activities") && <div className="card-ssf p-6"><p className="label-eyebrow">Activities</p><HtmlBlock html={L(p, "activities")} className="mt-2 text-sm text-slate-600" testid="project-activities" /></div>}
            </div>
          )}
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {p.impact && <div className="card-ssf p-6"><p className="label-eyebrow">Impact</p><HtmlBlock html={p.impact} className="mt-2 text-sm text-slate-600" /></div>}
            {p.outcome && <div className="card-ssf p-6"><p className="label-eyebrow">Outcome</p><HtmlBlock html={p.outcome} className="mt-2 text-sm text-slate-600" /></div>}
            {p.beneficiaries && <div className="card-ssf p-6"><p className="label-eyebrow">Beneficiaries</p><HtmlBlock html={p.beneficiaries} className="mt-2 text-sm text-slate-600" /></div>}
          </div>
          {Array.isArray(p.gallery) && p.gallery.length > 0 && (
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
              {p.gallery.map((g, i) => <LazyImg key={i} src={g} alt={`${p.title_en} ${i + 1}`} className="aspect-square rounded-xl object-cover" />)}
            </div>
          )}
          <div className="mt-10"><ShareButtons title={p.title_en} text={p.short_desc_en} /></div>
        </div>
      </section>
    </div>
  );
}
